/**
 * "Fill the gaps": the task score's ring, "N details missing", and ONE
 * question at a time (docs/TASK_FORM_FIELD_CONTRACT_2026-09-04.md §3–4).
 *
 * The questions and their order come from the server's `task_score`, so chat,
 * the planner and this card ask the same words for the same gap. Answering
 * writes the real field through the ordinary update path. Since 2026-09-05
 * the card is a carousel: Next/Back move between gaps and nothing is put
 * away. Answering never blocks Done or Start.
 */
import { useEffect, useMemo, useState } from 'react'
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react'

import { ParentPicker, type ParentType } from '../hierarchy/ParentPicker'
import { Button } from '../ui/Button'
import { Textarea } from '../ui/Textarea'
import { TaskService, type Task, type TaskScoreGap } from '../../services/taskService'
import {
  CONTEXT_OPTIONS, DEADLINE_TYPE_OPTIONS, DURATION_OPTIONS, ENERGY_OPTIONS, LIFE_AREA_OPTIONS,
  WHEN_OPTIONS, missingCaption, titleHint, type Choice,
} from '../../utils/taskForm'
import { TaskScoreRing } from './TaskScoreRing'


interface Props {
  task: Task
  /** Rendered inside another frame (the detail rail): no border, no padding, a smaller ring. */
  embedded?: boolean
  /** Called with the fresh task after an answer or a skip is saved. */
  onChanged: (task: Task) => void
}

function logEvent(event_type: string, data: Record<string, unknown>) {
  void fetch('/api/orchestrator/engagement-event', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_type, context: 'task_detail', data }),
  }).catch(() => undefined)
}

function Chips({ options, value, onChange, label }: { options: Choice[]; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <button key={o.value} type="button" aria-pressed={on} onClick={() => onChange(on ? '' : o.value)}
                  className={`min-h-touch px-3.5 py-2 rounded-full border text-sm font-medium transition-colors ${
                    on ? 'bg-brand text-on-brand border-brand' : 'bg-surface text-secondary border-strong hover:border-brand'}`}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function TaskGapsCard({ task, onChanged, embedded = false }: Props) {
  const score = task.task_score
  const [answer, setAnswer] = useState<Record<string, string>>({})
  const [parent, setParent] = useState<{ parent_type: ParentType | ''; parent_id: number | null }>({ parent_type: '', parent_id: null })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Owner 2026-09-05: a carousel, not a queue. Every gap stays reachable —
  // "Skip for now" used to rest a question for a week and felt like losing
  // it. Next/Back only move; answering removes the card because the gap
  // closed. The server's order is kept.
  // Every gap is a slide, the title included (2026-09-12). The title used to
  // be a separate nudge line because renaming lived on the edit page; with the
  // title edited in place that reason is gone, and the split made the card say
  // "6 details missing · 1 of 5" — two counts for one fact (North Star rule 2).
  // The title slide is an edit of the current title, saved on purpose, under
  // the question that names what it sets ("Name this as something to do").
  const gaps: TaskScoreGap[] = useMemo(() => (score?.scored ? score.missing : []), [score])
  const [idx, setIdx] = useState(0)
  useEffect(() => { if (idx > gaps.length - 1) setIdx(Math.max(0, gaps.length - 1)) }, [gaps.length, idx])
  useEffect(() => { setAnswer({}); setErr(null) }, [idx])
  const gap: TaskScoreGap | null = gaps[idx] ?? null
  const isTitle = gap?.fields[0] === 'title'
  const go = (d: number) => {
    if (gaps.length === 0) return
    if (gap) logEvent('task_prompt_advanced', { task_id: task.id, component: gap.component, direction: d > 0 ? 'next' : 'back' })
    setIdx((i) => (i + d + gaps.length) % gaps.length)
  }

  if (!score?.scored) return null

  const svc = new TaskService()
  const save = async (payload: Record<string, unknown>) => {
    setBusy(true); setErr(null)
    try {
      await svc.updateTask(task.id, payload)
      const fresh = await svc.getTask(task.id)
      onChanged(fresh)
      setAnswer({})
      setParent({ parent_type: '', parent_id: null })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (!gap) return
    const a = answer
    let payload: Record<string, unknown> | null = null
    switch (gap.kind) {
      case 'duration': {
        const n = Number(a.duration)
        if (n > 0) payload = { estimated_duration: n }
        break
      }
      case 'when': {
        const w = a.when
        if (w === 'today') payload = { target_window: 'today', user_tier: 'committed' }
        else if (w === 'someday') payload = { target_window: 'someday', user_tier: 'backlog' }
        else if (w === 'this_week' || w === 'this_month') payload = { target_window: w, user_tier: null }
        else if (w === 'date' && a.date) payload = { due_date: a.date, target_window: null }
        break
      }
      case 'belongs': {
        if (parent.parent_type && parent.parent_id) {
          payload = parent.parent_type === 'skill'
            ? { parent_skill_id: parent.parent_id, parent_goal_id: null }
            : { parent_goal_id: parent.parent_id, parent_skill_id: null }
        } else if (a.life_area) payload = { life_area: a.life_area }
        break
      }
      case 'stakes': {
        if (a.deadline_type) payload = { deadline_type: a.deadline_type, deadline_consequence: a.consequence?.trim() || undefined }
        break
      }
      case 'fit': {
        if (a.energy && a.context) payload = { energy_level_required: a.energy, context: a.context }
        break
      }
      default: {
        const text = (a.text ?? (isTitle ? task.title : '')).trim()
        if (!text) break
        if (isTitle && text === (task.title || '').trim()) { setErr('Change the title so it reads as something to do.'); return }
        const field = gap.fields[0] || 'acceptance_criteria'
        payload = { [field]: text }
      }
    }
    if (!payload) { setErr('Pick or type an answer first.'); return }
    logEvent('task_prompt_answered', { task_id: task.id, component: gap.component })
    await save(payload)
  }


  const caption = missingCaption(score)

  return (
    <div className={embedded ? '' : 'rounded-xl border border-brand-subtle bg-surface p-4 sm:p-5'} data-testid="task-gaps-card">
      <div className="flex items-center gap-3">
        <TaskScoreRing score={score} size={embedded ? 36 : 44} />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-primary">{gap ? 'Fill the gaps' : 'Ready to plan'}</div>
          <div className="text-xs text-muted">{caption}</div>
        </div>
        {gaps.length > 1 && (
          <div className="ml-auto flex items-center gap-1 text-app-micro text-muted" data-testid="task-gaps-nav">
            <button type="button" onClick={() => go(-1)} aria-label="Previous question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronLeft className="h-4 w-4" /></button>
            <span className="tabular-nums">{idx + 1} of {gaps.length}</span>
            <button type="button" onClick={() => go(1)} aria-label="Next question" className="rounded p-1 hover:bg-surface hover:text-primary"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {gap && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-primary">{gap.question}</p>

          {isTitle && (
            <div className="space-y-1">
              <input type="text" value={answer.text ?? task.title} onChange={(e) => setAnswer({ ...answer, text: e.target.value })}
                     placeholder="Say it as something to do — Call…, Write…, Book…"
                     className="w-full px-3 py-2 border border-strong rounded-lg bg-surface text-primary text-sm" aria-label={gap.question} data-testid="task-gaps-title" />
              <p className={`text-app-micro ${titleHint(answer.text ?? task.title) ? 'text-warn' : 'text-muted'}`}>{titleHint(answer.text ?? task.title) || 'Starts with a verb, names the outcome.'}</p>
            </div>
          )}

          {gap.kind === 'duration' && (
            <Chips label="How long" options={DURATION_OPTIONS} value={answer.duration || ''} onChange={(v) => setAnswer({ ...answer, duration: v })} />
          )}
          {gap.kind === 'when' && (
            <div className="space-y-2">
              <Chips label="When" options={WHEN_OPTIONS} value={answer.when || ''} onChange={(v) => setAnswer({ ...answer, when: v })} />
              {answer.when === 'date' && (
                <input type="date" value={answer.date || ''} onChange={(e) => setAnswer({ ...answer, date: e.target.value })}
                       className="px-3 py-2 border border-strong rounded-lg bg-surface text-primary" aria-label="Which date" />
              )}
            </div>
          )}
          {gap.kind === 'belongs' && (
            <div className="space-y-3">
              <ParentPicker allowedTypes={['goal', 'skill']} parentType={parent.parent_type} parentId={parent.parent_id}
                            onChange={(f) => setParent({ parent_type: (f.parent_type as ParentType) || '', parent_id: f.parent_id ?? null })} />
              {!parent.parent_id && (
                <div>
                  <div className="text-xs text-muted mb-1.5">Or, if it is just for you — which part of life?</div>
                  <Chips label="Life area" options={LIFE_AREA_OPTIONS} value={answer.life_area || ''} onChange={(v) => setAnswer({ ...answer, life_area: v })} />
                </div>
              )}
            </div>
          )}
          {gap.kind === 'stakes' && (
            <div className="space-y-2">
              <Chips label="How firm is the date" options={DEADLINE_TYPE_OPTIONS} value={answer.deadline_type || ''} onChange={(v) => setAnswer({ ...answer, deadline_type: v })} />
              <input type="text" value={answer.consequence || ''} onChange={(e) => setAnswer({ ...answer, consequence: e.target.value })}
                     placeholder="If it slips…" className="w-full px-3 py-2 border border-strong rounded-lg bg-surface text-primary text-sm" aria-label="If it slips" />
            </div>
          )}
          {gap.kind === 'fit' && (
            <div className="space-y-2">
              <Chips label="Energy" options={ENERGY_OPTIONS} value={answer.energy || ''} onChange={(v) => setAnswer({ ...answer, energy: v })} />
              <Chips label="Where" options={CONTEXT_OPTIONS} value={answer.context || ''} onChange={(v) => setAnswer({ ...answer, context: v })} />
            </div>
          )}
          {!isTitle && (gap.kind === 'text' || gap.kind === 'choice' || gap.kind === 'why') && (
            <Textarea rows={2} value={answer.text ?? ''}
                      onChange={(e) => setAnswer({ ...answer, text: e.target.value })}
                      placeholder={gap.component === 'done_means' ? 'e.g. the form is filed and the receipt is in the drawer' : gap.kind === 'why' ? 'In your words — one line is plenty' : 'One line is plenty'}
                      aria-label={gap.question} />
          )}

          {err && <p className="text-xs text-bad" role="alert">{err}</p>}
          <div className="flex items-center gap-2">
            <Button size="xs" variant="outline" className="hover:bg-brand-subtle" onClick={() => void submit()} disabled={busy}>Save</Button>
            {gaps.length > 1 && (
              <button type="button" onClick={() => go(1)} disabled={busy}
                      className="min-h-touch px-3 text-sm text-muted hover:text-secondary" data-testid="task-gaps-next">
                Next question
              </button>
            )}
            <span className="ml-auto inline-flex items-center gap-1 text-app-micro text-muted"><Sparkles className="w-3 h-3" aria-hidden="true" /> {gap.tier === 'B' ? 'helps me plan it' : 'helps me plan it well'}</span>
          </div>
          {gaps.length > 1 && (
            <div className="flex items-center justify-center gap-1.5" aria-hidden="true">
              {gaps.map((g, i) => (
                <button key={g.component} type="button" tabIndex={-1} onClick={() => setIdx(i)} title={g.question} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-brand' : 'w-1.5 bg-soft hover:bg-strong'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
